import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E-M7 — PRISME. Le professeur de la ZONE à évacuer.
 *
 *  Le Sabreur projette une lame de mi-terrain qui balaie : on lit son sens et on
 *  se glisse derrière. Le Prisme fait tout l'inverse — il ne balaie pas, il ne
 *  bouge pas : il PLANTE une colonne de lumière sur TOUTE la hauteur du terrain,
 *  la tient une durée franche, puis l'éteint. Ancré, immobile, il transforme sa
 *  propre abscisse en couloir de mort.
 *
 *  ── Ce qu'il enseigne ──
 *  Qu'un danger peut être une ZONE qu'on évacue à l'avance, et non un projectile
 *  qu'on esquive à l'instant. La lame du Sabreur se lit en mouvement ; la colonne
 *  du Prisme se lit en anticipation — « cette verticale-là va devenir mortelle,
 *  je dégage pendant qu'il est encore temps ». C'est la leçon spatiale la plus
 *  exigeante du lot, d'où sa place en dernier.
 *
 *  ── ⚠️ Il DOIT être télégraphié, sinon c'est une mort non attribuable ──
 *  Une colonne pleine hauteur atteint la rangée du joueur (sous −180), là où le
 *  §6.5 interdit tout tir qui n'a pas prévenu 1,4 s à l'avance. Le mécanisme :
 *  le HAUT de la colonne (au-dessus de −180, toujours licite) s'allume d'abord et
 *  reste là 1,5 s — c'est lui, visible et létal seulement en zone haute, qui
 *  ANNONCE la colonne ; le BAS ne descend dans la rangée du joueur qu'à
 *  l'expiration de cet avertissement. Le joueur voit donc exactement quelle
 *  verticale fuir, et il a le temps de le faire (voir `beamWarn` et
 *  `EbMode.Beam`).
 *
 *  ── Pourquoi ancré et immobile ──
 *  Un Prisme mobile serait un Sabreur pleine hauteur, c'est-à-dire un mur mouvant
 *  inévitable. Fixe, il pose une question simple et honnête : « es-tu dans cette
 *  colonne ? » — et laisse toujours le reste du terrain ouvert.
 *
 *  ── La pulsation ──
 *  1,7 s de colonne pleine toutes les 4,2 s (plus 1,5 s d'avertissement). Les
 *  ~1 s de noir entre deux cycles rouvrent la colonne : sans elles, ce serait un
 *  mur permanent, donc une zone morte. */
export default defineEnemy({
  id: 'prisme',
  rank: Rank.Heavy,
  hp: 300,
  visualRadius: 30,
  score: 360,
  telegraph: 1.2,
  drops: 3,
  // Ancré haut (Y = +380) : la colonne part de tout en haut et descend jusque
  // sous le joueur. Immobile — c'est une zone, pas un balayage.
  motion: { kind: Motion.Anchor, speed: 200, holdY: 380 },
  emitter: {
    pattern: Pattern.Beam,
    // Cycle : avertissement (1,5) + colonne pleine (1,7) = 3,2 s de segment,
    // sous les 4,2 s du cycle — deux colonnes ne se chevauchent donc jamais.
    every: 4.2,
    // 46 segments sur 900 u = un pas de 20 u, sous le diamètre d'un projectile
    // (22 u) : la colonne est pleine, sans pointillé où le joueur se faufilerait.
    count: 46,
    // 900 u depuis Y = +380 → le bas atteint Y = −520, sous la rangée du joueur
    // (~−380) : la colonne couvre vraiment toute la hauteur utile.
    beamLen: 900,
    // Colonne pleine tenue 1,7 s une fois l'avertissement écoulé.
    beamLife: 1.7,
    // ⚠️ 1,5 s d'avertissement (≥ 1,4 s, §6.5) avant que la portion sous −180 ne
    // devienne létale. En dessous de 1,4 la colonne redeviendrait injuste.
    beamWarn: 1.5,
    // `speed` ignoré (segments ancrés, vitesse nulle) mais laissé nul par
    // honnêteté.
    speed: 0,
    firstDelay: 1.5,
    family: Fam.EnemyRed,
  },
});
